import React, { useState, useEffect } from 'react';
import {
	Dialog,
	DialogContent,
	Box,
	Typography,
	TextField,
	Button,
	Alert,
	useMediaQuery,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import theme from '../../util/theme/theme';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';

interface NewsletterPopupProps {
	open: boolean;
	onClose: () => void;
	onSubscribe?: (email: string) => Promise<void> | void;
	country?: string;
	defaultEmail?: string;
}

const NewsletterPopup: React.FC<NewsletterPopupProps> = ({
	open,
	onClose,
	onSubscribe,
	country = 'Your country',
	defaultEmail = '',
}) => {
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [email, setEmail] = useState(defaultEmail);
	const [loading, setLoading] = useState(false);
	const [subscribed, setSubscribed] = useState(false);
	const [error, setError] = useState('');

	// Sync email when defaultEmail changes
	useEffect(() => {
		if (open && defaultEmail) {
			setEmail(defaultEmail);
		}
	}, [open, defaultEmail]);

	const handleClose = () => {
		if (!loading) {
			setEmail(defaultEmail);
			setError('');
			setSubscribed(false);
			onClose();
		}
	};

	const validateEmail = (email: string) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const handleSubscribe = async () => {
		setError('');

		if (!email.trim()) {
			setError('Please enter your email address');
			return;
		}

		if (!validateEmail(email)) {
			setError('Please enter a valid email address');
			return;
		}

		try {
			setLoading(true);
			if (onSubscribe) {
				await onSubscribe(email);
			}
			setSubscribed(true);
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: 'Failed to subscribe. Please try again.'
			);
		} finally {
			setLoading(false);
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && email && !loading) {
			handleSubscribe();
		}
	};

	const content = (
		<>
			<DialogContent
				sx={{
					textAlign: 'center',
					pt: 4,
					pb: 4,
					px: 3,
				}}
			>
				{subscribed ? (
					<Box display="flex" flexDirection="column" alignItems="center" gap={2}>
						<CheckCircleOutlineIcon
							sx={{
								fontSize: 64,
								color: 'var(--color-primary)',
							}}
						/>
						<Typography
							sx={{
								fontWeight: 700,
								fontSize: 22,
								color: 'var(--color-default-text)',
								mb: 1,
							}}
						>
							Thank You!
						</Typography>
						<Typography
							sx={{
								fontSize: 16,
								color: 'var(--color-text-muted)',
								mb: 3,
							}}
						>
							You've successfully subscribed to our newsletter. We'll notify you
							as soon as we expand to your region.
						</Typography>
						<Button
							onClick={handleClose}
							variant="contained"
							sx={{
								background: 'var(--color-primary)',
								color: '#fff',
								borderRadius: 8,
								fontWeight: 600,
								textTransform: 'none',
								fontSize: 16,
								px: 4,
								py: 1.2,
								boxShadow: 'none',
								'&:hover': {
									background: '#009980',
									boxShadow: 'none',
								},
							}}
						>
							Close
						</Button>
					</Box>
				) : (
					<Box display="flex" flexDirection="column" gap={2.5}>
						<Typography
							sx={{
								fontWeight: 700,
								fontSize: 22,
								color: 'var(--color-default-text)',
								mb: 1,
							}}
						>
							Coming Soon!
						</Typography>

						<Typography
							sx={{
								fontSize: 16,
								color: 'var(--color-text-muted)',
								lineHeight: 1.6,
							}}
						>
							{country} is not supported yet, but we're working hard to bring our
							services to you soon. Subscribe to our newsletter to be the first to
							know when we launch in your region!
						</Typography>

						{error && (
							<Alert severity="error" sx={{ borderRadius: 2, fontSize: 14 }}>
								{error}
							</Alert>
						)}

						<Box display="flex" flexDirection="column" gap={2} mt={1}>
							<Box>
								<Typography
									fontSize={14}
									color="var(--color-text-muted)"
									textAlign="left"
									sx={{ mb: 1 }}
								>
									Email Address{' '}
									<span style={{ color: '#ff5252' }}>*</span>
								</Typography>
								<TextField
									placeholder="Enter your email"
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									onKeyPress={handleKeyPress}
									disabled={loading}
									fullWidth
									size="small"
									InputProps={{
										style: {
											borderRadius: 8,
										},
									}}
									sx={{
										'& .MuiInputBase-input::placeholder': {
											opacity: 0.6,
										},
									}}
								/>
							</Box>
						</Box>

						<Box
							display="flex"
							gap={2}
							mt={2}
							sx={{
								flexDirection: { xs: 'column', sm: 'row' },
							}}
						>
							<Button
								onClick={handleClose}
								disabled={loading}
								variant="contained"
								fullWidth
								sx={{
									background: 'var(--color-container-background)',
									color: 'var(--color-default-text)',
									borderRadius: 8,
									fontWeight: 600,
									textTransform: 'none',
									fontSize: 16,
									py: 1.1,
									boxShadow: 'none',
									'&:hover': {
										background: '#E8E9EB',
										boxShadow: 'none',
									},
									'&:disabled': {
										background: 'var(--color-container-background)',
										color: '#A0A0A0',
									},
								}}
							>
								Cancel
							</Button>
							<Button
								onClick={handleSubscribe}
								disabled={loading || !email}
								variant="contained"
								fullWidth
								sx={{
									background: 'var(--color-primary)',
									color: '#fff',
									borderRadius: 8,
									fontWeight: 600,
									textTransform: 'none',
									fontSize: 16,
									py: 1.1,
									boxShadow: 'none',
									'&:hover': {
										background: '#009980',
										boxShadow: 'none',
									},
									'&:disabled': {
										background: '#CCE4DE',
										color: '#fff',
									},
								}}
							>
								{loading ? 'Subscribing...' : 'Subscribe'}
							</Button>
						</Box>

						<Typography
							sx={{
								fontSize: 12,
								color: '#A0A0A0',
								mt: 1,
							}}
						>
							We'll never spam you. Unsubscribe at any time.
						</Typography>
					</Box>
				)}
			</DialogContent>
		</>
	);

	return isMobile ? (
		<BottomSheetDialog open={open} onClose={handleClose} fitContent>
			{content}
		</BottomSheetDialog>
	) : (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				style: {
					borderRadius: 16,
					background: 'var(--color-page-background)',
				},
			}}
		>
			{content}
		</Dialog>
	);
};

export default NewsletterPopup;
